/**
 * Normalizes Meta and TikTok catalog ledger rows into one publisher-agnostic shape so
 * the catalog Ads Manager components never branch on publisher field names.
 *
 * PRODUCT_SET rows carry { productSetName, catalogId, businessCenterId? (TikTok),
 * filterHash?, filterProductCount?, adoptedFromFry? }. Each product is an ASSET row under
 * it (parentPublisherEntityId === the product set id) whose metadata is
 * { productId, refTag, gti, data }, `data` being the exact payload sent to the publisher:
 *   Meta:   id, title, availability ('in stock'/'out of stock'), image_link, video[{url}], link, price
 *   TikTok: sku_id, title, availability (IN_STOCK/OUT_OF_STOCK), image_url, video_url,
 *           landing_page.landing_page_url, price_info.price
 */
import type { PublisherEntityDto } from 'src/store/generated/leela-api-generated';

export type CatalogAvailability = 'IN_STOCK' | 'OUT_OF_STOCK';

export interface CatalogProduct {
  productId?: string;
  refTag?: string;
  gti?: string;
  title?: string;
  availability?: CatalogAvailability;
  imageUrl?: string;
  videoUrl?: string;
  isVideo: boolean;
  link?: string;
  /** The payload sent to the publisher, flattened to dotted leaf keys for display. */
  payload?: Record<string, unknown>;
}

export interface CatalogProductSetInfo {
  name?: string;
  catalogId?: string;
  businessCenterId?: string;
  filterProductCount?: number;
  adoptedFromFry: boolean;
}

type UnknownRecord = Record<string, unknown>;

function asRecord (value: unknown): UnknownRecord | undefined {
  return typeof value === 'object' && value !== null && !Array.isArray(value) ? value as UnknownRecord : undefined;
}

function readString (...values: unknown[]): string | undefined {
  for (const value of values) {
    if (typeof value === 'string' && value.trim() !== '') return value;
  }
  return undefined;
}

function readFirstVideoUrl (video: unknown): string | undefined {
  if (!Array.isArray(video)) return undefined;
  for (const item of video) {
    const url = readString(asRecord(item)?.url);
    if (url) return url;
  }
  return undefined;
}

/** Maps Meta's 'in stock'/'out of stock' and TikTok's IN_STOCK/OUT_OF_STOCK to one value. */
export function normalizeAvailability (value: unknown): CatalogAvailability | undefined {
  if (typeof value !== 'string') return undefined;
  const normalized = value.trim().toUpperCase().replace(/[\s-]+/g, '_');
  if (normalized === 'IN_STOCK') return 'IN_STOCK';
  if (normalized === 'OUT_OF_STOCK') return 'OUT_OF_STOCK';
  return undefined;
}

/**
 * Flattens a payload into dotted leaf keys (`landing_page.ios_url`, `video[0].url`) so
 * nested publisher fields keep their full path instead of colliding on a shared leaf name.
 * Null/undefined leaves and empty containers are dropped.
 */
export function flattenPayload (value: unknown, prefix = ''): Record<string, unknown> {
  const result: Record<string, unknown> = {};
  const visit = (node: unknown, path: string): void => {
    if (node === null || node === undefined) return;
    if (Array.isArray(node)) {
      node.forEach((item, index) => { visit(item, `${path}[${index}]`); });
      return;
    }
    const record = asRecord(node);
    if (record) {
      Object.entries(record).forEach(([key, child]) => { visit(child, path ? `${path}.${key}` : key); });
      return;
    }
    if (path) result[path] = node;
  };
  visit(value, prefix);
  return result;
}

/** Reads one catalog product (ASSET row) into the publisher-agnostic shape. */
export function normalizeCatalogProduct (entity: Pick<PublisherEntityDto, 'publisherMetadata'>): CatalogProduct {
  const meta = asRecord(entity.publisherMetadata) ?? {};
  const data = asRecord(meta.data);
  const landingPage = asRecord(data?.landing_page);
  const videoUrl = readString(data?.video_url, readFirstVideoUrl(data?.video));
  const payload = data ? flattenPayload(data) : undefined;

  return {
    availability: normalizeAvailability(data?.availability),
    gti: readString(meta.gti),
    imageUrl: readString(data?.image_link, data?.image_url),
    isVideo: videoUrl !== undefined,
    link: readString(data?.link, landingPage?.landing_page_url),
    payload: payload && Object.keys(payload).length > 0 ? payload : undefined,
    productId: readString(meta.productId, data?.id, data?.sku_id),
    refTag: readString(meta.refTag),
    title: readString(data?.title),
    videoUrl,
  };
}

/**
 * The status a catalog product shows: its ledger row with entityStatus replaced by the
 * availability actually sent to the publisher. A sports product outside its window is
 * ledger IN_STOCK but published out of stock, so it reads (and dims as) Removed. Shared by
 * the product row's badge and the product set card's dimming so they never disagree.
 */
export function withPublishedAvailability<T extends Pick<PublisherEntityDto, 'entityStatus' | 'publisherMetadata'>> (entity: T): T {
  const availability = normalizeCatalogProduct(entity).availability;
  return availability === undefined ? entity : { ...entity, entityStatus: availability };
}

/** Reads a catalog PRODUCT_SET row's display fields. */
export function readProductSetInfo (entity: Pick<PublisherEntityDto, 'publisherMetadata'>): CatalogProductSetInfo {
  const meta = asRecord(entity.publisherMetadata) ?? {};
  const count = meta.filterProductCount;
  return {
    adoptedFromFry: meta.adoptedFromFry === true,
    businessCenterId: readString(meta.businessCenterId),
    catalogId: readString(meta.catalogId),
    filterProductCount: typeof count === 'number' && Number.isFinite(count) ? count : undefined,
    name: readString(meta.productSetName),
  };
}

/** Only http(s) destinations are rendered as clickable links. */
export function isSafeHttpUrl (url: string | undefined): url is string {
  if (!url) return false;
  try {
    const { protocol } = new URL(url);
    return protocol === 'https:' || protocol === 'http:';
  } catch {
    return false;
  }
}
